import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInSection } from '../components/FadeInSection';

interface FaqItem {
  question: string;
  answer: string;
}

const FAQS_GENERAL: FaqItem[] = [
  {
    question: 'What should I bring to my first appointment?',
    answer:
      'Please bring your medical aid card, a valid form of ID, any previous scan or lab results, a list of current medications, and your referral letter if applicable. If you are pregnant, bring your Road to Health booklet if you have one.',
  },
  {
    question: 'Do I need a referral to see Dr Khan?',
    answer:
      'A referral is not required for a private consultation, but some medical aids may require one for reimbursement. Check with your medical aid before your appointment.',
  },
  {
    question: 'How do I get my results?',
    answer:
      "Results are discussed at a follow-up consultation or communicated via phone for routine results. Dr Khan's practice will contact you when results are available.",
  },
  {
    question: 'What medical aids does Dr Khan accept?',
    answer:
      "Dr Khan is registered with most major medical aids including Discovery, Bonitas, Medihelp, Momentum, and Fedhealth. Please confirm your specific plan's cover with your medical aid before your appointment.",
  },
  {
    question: 'Is there parking available?',
    answer: 'Yes, free parking is available on-site at both Melomed Gatesville and Melomed Mitchell\'s Plain.',
  },
];

const FAQS_OBSTETRICS: FaqItem[] = [
  {
    question: 'When should I book my first antenatal appointment?',
    answer:
      'As soon as you have a positive pregnancy test — ideally before 10 weeks. Early booking allows for first trimester screening, dating scans, and early identification of any risk factors.',
  },
  {
    question: 'How many antenatal visits will I need?',
    answer:
      'For a low-risk pregnancy, you can expect approximately 8–10 visits throughout your pregnancy. High-risk pregnancies may require more frequent monitoring. A typical schedule is: booking visit (before 10 weeks), 12 weeks (NT scan), 16 weeks, 20 weeks (anomaly scan), 24 weeks, 28 weeks, 32 weeks, 36 weeks, 38 weeks, 40 weeks.',
  },
  {
    question: 'What supplements should I take during pregnancy?',
    answer:
      'Folic acid supplementation is recommended before conception and during early pregnancy. The appropriate dose depends on your individual risk factors — Dr Khan will advise you. Do not take any supplements or medications without discussing with your doctor first.',
  },
  {
    question: 'What symptoms should I be concerned about during pregnancy?',
    answer:
      'Go to your nearest emergency room immediately if you experience: heavy vaginal bleeding, severe abdominal pain, severe headache with visual disturbance, sudden swelling of the face or hands, reduced or absent foetal movements after 28 weeks, leaking fluid, or fever above 38°C.',
  },
  {
    question: 'Can I exercise during pregnancy?',
    answer:
      'Yes — regular moderate exercise is encouraged for most pregnant women. Walking, swimming, and prenatal yoga are ideal. Avoid contact sports, high-impact activities, and exercises that involve lying flat on your back after 16 weeks. Always discuss your exercise plan with Dr Khan first.',
  },
];

const FAQS_GYNAE: FaqItem[] = [
  {
    question: 'How often should I have a Pap smear?',
    answer:
      'Current South African guidelines recommend a Pap smear every 3 years for women aged 30–65 who have had normal results. Women who are HIV-positive or immunocompromised should be screened more frequently. Speak to Dr Khan about the right schedule for you.',
  },
  {
    question: 'What is endometriosis?',
    answer:
      'Endometriosis is a condition where tissue similar to the lining of the uterus grows outside the uterus — commonly on the ovaries, fallopian tubes, and pelvic lining. It can cause severe period pain, pelvic pain, painful intercourse, and fertility problems. It is diagnosed by laparoscopy and managed with medication or surgery depending on severity.',
  },
  {
    question: 'What contraceptive options are available?',
    answer:
      "Dr Khan can advise on and fit a range of contraceptive options including: the oral contraceptive pill, the hormonal IUD (Mirena), the copper IUD, the contraceptive implant (Implanon), the injectable (Depo-Provera), and barrier methods. The best option depends on your health history, lifestyle, and future pregnancy plans.",
  },
  {
    question: 'What is menopause and when does it happen?',
    answer:
      'Menopause is defined as 12 consecutive months without a period and typically occurs between ages 45–55. Perimenopause (the transition phase) can begin several years earlier. Common symptoms include hot flushes, night sweats, mood changes, vaginal dryness, and sleep disturbance. Hormone Replacement Therapy (HRT) and non-hormonal options are available — Dr Khan will help you find the right approach.',
  },
];

function FaqAccordion({ title, items }: { title: string; items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="mb-8">
      <h3
        className="font-heading text-xl mb-4"
        style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
      >
        {title}
      </h3>
      <div className="space-y-2">
        {items.map((faq, idx) => (
          <div
            key={idx}
            className="rounded-lg overflow-hidden border"
            style={{ borderColor: openIndex === idx ? 'var(--color-gold)' : '#e5e7eb' }}
          >
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors"
              style={{
                backgroundColor: openIndex === idx ? 'var(--color-gold-light)' : '#fff',
                fontFamily: 'var(--font-body)',
              }}
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              aria-expanded={openIndex === idx}
            >
              <span
                className="font-body font-medium text-sm"
                style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
              >
                {faq.question}
              </span>
              <motion.svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0"
                style={{ color: 'var(--color-gold)' }}
                animate={{ rotate: openIndex === idx ? 180 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </motion.svg>
            </button>
            <AnimatePresence initial={false}>
              {openIndex === idx && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div
                    className="px-5 pb-4 pt-2 text-sm font-body leading-relaxed border-l-4"
                    style={{
                      color: 'var(--color-navy)',
                      fontFamily: 'var(--font-body)',
                      opacity: 0.8,
                      borderLeftColor: 'var(--color-gold)',
                    }}
                  >
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

const AFTER_HOURS = process.env.REACT_APP_AFTER_HOURS_NUMBER || '+27820000000';
const AFTER_HOURS_DISPLAY = AFTER_HOURS.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');

export function PatientInfoPage() {
  useEffect(() => {
    document.title = 'Patient Information | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Patient information for IKhanGynae — what to bring, what to expect, emergency contacts and frequently asked questions.'
      );
    }
  }, []);

  const whatToBring = [
    'Medical aid card',
    'Valid form of ID (ID book or passport)',
    'Any previous scan or lab results',
    'List of current medications',
    'Referral letter (if applicable)',
    'Road to Health booklet (if pregnant)',
  ];

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            Everything you need to know
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Patient Information
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
        </div>
      </section>

      {/* ── What to Bring ── */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                color: 'var(--color-navy)',
              }}
            >
              What to Bring to Your First Appointment
            </h2>
            <div className="flex flex-wrap gap-3">
              {whatToBring.map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 rounded-full text-sm font-body font-medium"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-primary)',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── What to Expect ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-4"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                color: 'var(--color-navy)',
              }}
            >
              What to Expect at Your First Visit
            </h2>
            <p className="text-sm leading-relaxed mb-3 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
              Your first consultation with Dr Khan will cover a full medical and gynaecological history, your concerns, and any relevant examinations or investigations.
            </p>
            <p className="text-sm leading-relaxed mb-3 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
              For obstetric bookings, a dating scan and first trimester blood panel are usually arranged at or shortly after the first visit.
            </p>
            <p className="text-sm leading-relaxed font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
              Please arrive 10 minutes early to complete registration paperwork.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Emergency Contact ── */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div
              className="rounded-xl p-6 border-l-4"
              style={{ backgroundColor: '#FFF8E1', borderLeftColor: '#F59E0B', border: '1px solid #FCD34D', borderLeft: '4px solid #F59E0B' }}
            >
              <div className="flex items-start gap-3">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 mt-0.5"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <h3
                    className="font-heading text-xl mb-2"
                    style={{ fontFamily: 'var(--font-heading)', color: '#92400E', fontWeight: 500 }}
                  >
                    Emergency Contact
                  </h3>
                  <p className="text-sm font-body mb-2" style={{ color: '#78350F', fontFamily: 'var(--font-body)' }}>
                    <strong>For emergencies, please go directly to your nearest emergency room.</strong>
                  </p>
                  <p className="text-sm font-body mb-2" style={{ color: '#78350F', fontFamily: 'var(--font-body)' }}>
                    For urgent after-hours concerns related to an active pregnancy or recent procedure, call:{' '}
                    <a href={`tel:${AFTER_HOURS}`} className="font-bold underline">{AFTER_HOURS_DISPLAY}</a>
                  </p>
                  <p className="text-sm font-body font-semibold" style={{ color: '#92400E', fontFamily: 'var(--font-body)' }}>
                    Do not use WhatsApp or email for emergencies.
                  </p>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── New Patient Registration ── */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <div
              className="rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}
            >
              <div>
                <h3
                  className="font-heading text-xl mb-1"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                >
                  New Patients
                </h3>
                <p className="text-sm font-body" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
                  New patients can pre-register online to save time on the day of your visit.
                </p>
              </div>
              <Link
                to="/register"
                className="flex-shrink-0 px-6 py-3 rounded-md font-semibold font-body text-sm hover:opacity-90 transition-opacity"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: '#fff',
                  fontFamily: 'var(--font-body)',
                }}
              >
                Register Online
              </Link>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-10"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
              }}
            >
              Frequently Asked Questions
            </h2>
          </FadeInSection>
          <FaqAccordion title="General" items={FAQS_GENERAL} />
          <FaqAccordion title="Obstetrics &amp; Pregnancy" items={FAQS_OBSTETRICS} />
          <FaqAccordion title="Gynaecology" items={FAQS_GYNAE} />
        </div>
      </section>
    </div>
  );
}
